import React, { useState } from 'react';
import { Loader2, X, ChevronUp, ChevronDown, Plus, FileText } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../ui/button';
import { useSetProcessDocuments } from '../../services/documents';
import useEscapeToClose from './useEscapeToClose';

const NeutralButton = ({ children, ...props }) => (
  <button
    type="button"
    className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
    {...props}
  >
    {children}
  </button>
);

// Assign + order the documents in a (fixed) process group. Groups are static —
// this only manages which documents are in them and in what sign order.
export const ManageDocumentsModal = ({ process, uncategorized, onClose }) => {
  const [included, setIncluded] = useState(process.documents || []);
  const [available, setAvailable] = useState(uncategorized || []);
  const setDocs = useSetProcessDocuments();

  useEscapeToClose(onClose);

  const add = (doc) => {
    setIncluded((p) => [...p, doc]);
    setAvailable((p) => p.filter((d) => d.documentId !== doc.documentId));
  };
  const remove = (doc) => {
    setAvailable((p) => [...p, doc]);
    setIncluded((p) => p.filter((d) => d.documentId !== doc.documentId));
  };
  const move = (i, dir) =>
    setIncluded((p) => {
      const a = [...p];
      const j = i + dir;
      if (j < 0 || j >= a.length) return a;
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });

  const save = async () => {
    try {
      await setDocs.mutateAsync({
        processId: process.processId,
        documentIds: included.map((d) => d.documentId),
      });
      toast.success('Documents updated.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Save failed.');
    }
  };

  const docRow = (d, content) => (
    <div key={d.documentId} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate text-foreground">{d.name}</span>
      {content}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1060] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-xl rounded-xl border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-base font-semibold text-foreground">Manage: {process.name}</h3>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <p className="text-xs text-muted-foreground">
            Reorder or swap documents any time — new signing sessions use the latest set.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-secondary/20 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                In this group · sign order
              </div>
              <div className="max-h-64 min-h-[7rem] space-y-1.5 overflow-y-auto pr-1">
                {included.length === 0 ? (
                  <div className="flex h-28 items-center justify-center px-2 text-center text-xs text-muted-foreground">
                    Add documents from the right →
                  </div>
                ) : (
                  included.map((d, i) =>
                    docRow(
                      d,
                      <div className="flex items-center gap-0.5">
                        <span className="mr-1 text-xs text-muted-foreground">{i + 1}</span>
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${d.name} up`} className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30">
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === included.length - 1} aria-label={`Move ${d.name} down`} className="rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-30">
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => remove(d)} aria-label={`Remove ${d.name} from this group`} className="rounded p-1 text-muted-foreground hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>,
                    ),
                  )
                )}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-secondary/20 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Available
              </div>
              <div className="max-h-64 min-h-[7rem] space-y-1.5 overflow-y-auto pr-1">
                {available.length === 0 ? (
                  <div className="flex h-28 items-center justify-center px-2 text-center text-xs text-muted-foreground">
                    All documents are already in a group.
                  </div>
                ) : (
                  available.map((d) =>
                    docRow(
                      d,
                      <button type="button" onClick={() => add(d)} aria-label={`Add ${d.name} to this group`} className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary">
                        <Plus className="h-4 w-4" />
                      </button>,
                    ),
                  )
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <NeutralButton onClick={onClose} disabled={setDocs.isLoading}>Cancel</NeutralButton>
            <Button onClick={save} disabled={setDocs.isLoading} className="hover:bg-primary hover:opacity-100">
              {setDocs.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
